import { ApplicationConfig, provideBrowserGlobalErrorListeners } from "@angular/core";
import { provideRouter } from "@angular/router";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { provideTranslateService, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader, provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideMarkdown } from "ngx-markdown";
import "@progress/kendo-angular-intl/locales/it/all";
import "@progress/kendo-angular-intl/locales/de/all";

import { routes } from "./app.routes";
import { embedFontSize, embedPrimaryColor } from "./embed-options";
import { identityInterceptor, provideChatbot } from "atl-chatbot-lib";
import { environment } from "../environments/environment";

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideRouter(routes),
        provideChatbot({
            apiUrl: environment.apiUrl,
            // This application is the one an iframe host loads, which is what gives it a free
            // question limit and the lead capture that follows it, and what lets it fill its
            // frame instead of placing itself on screen.
            surface: "iframe",
            // The embedding page's own colour, so the widget matches the site it sits on. Read
            // from the frame's URL rather than posted later: a colour that arrives after the first
            // paint is a visible flash of somebody else's brand.
            primaryColor: embedPrimaryColor(),
            // The embedding page's base text size, already resolved by that page: a variable
            // belongs to the document that declares it, and this frame is a different document.
            fontSize: embedFontSize() ?? undefined,
            allowedHostOrigins: environment.allowedHostOrigins,
            // Wired to the local build flag and to nothing else. Every other build serves the API
            // over TLS, and credentials are withheld from a plain HTTP request without this.
            allowInsecureTransport: environment.local,
            // The one audience this application serves: a visitor nobody issued a credential to.
            // The name travels to the backend as written, so it has to be the name that backend
            // knows for this product.
            domains: {
                web: {
                    agent: "web_agent",
                    identity: "anonymous",
                    allowsSessionManagement: false,
                    showsAgentPicker: false,
                },
            },
        }),
        // Required by the Kendo Drawer behind the conversations sidebar: it injects `AnimationBuilder`
        // for its slide-in, and without this provider Angular throws NG03600 while constructing it,
        // which takes the whole component tree down, not just the animation.
        provideAnimations(),
        provideMarkdown(),
        provideHttpClient(withInterceptors([identityInterceptor])),
        // The loader no longer takes its paths through a factory: it reads them from
        // `provideTranslateHttpLoader` below.
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useClass: TranslateHttpLoader,
            },
            fallbackLang: "en",
        }),
        provideTranslateHttpLoader({ prefix: "./assets/i18n/", suffix: ".json" }),
    ],
};
